import { ChevronDownIcon } from "@heroicons/react/24/outline";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { InputFormBlock } from "components/app/input-form-block";
import { nonVisitActivityCategories } from "components/details-page-v-next/activity-parent";
import {
    ActivityFeedRowBody,
    ActivityRowsList,
} from "components/details-page-v-next/activity-rows";
import { ActivityFilterChips } from "components/details-page-v-next/recent-activities";
import {
    filterRecentActivityItems,
    RecentActivityFilterKey,
    recentActivityItemDisplay,
    sortRecentActivityItems,
} from "components/details-page-v-next/recent-activities-feed";
import { Header } from "components/header";
import { Badge } from "components/ui/badge";
import { BlankSlate } from "components/ui/blank-slate";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import * as React from "react";

const INITIAL_VISIBLE_COUNT = 3;
const SHOW_MORE_INCREMENT = 10;

export type SharedRecentActivity = inferProcedureOutput<
    AppRouter["sharedPipeline"]["lead"]
>["recentActivities"][number];

const importantBadge = <Badge type="highlight" value="Important" />;

const SharedRecentActivityRow: React.FC<{ activity: SharedRecentActivity }> = ({
    activity,
}) => (
    <li>
        <ActivityFeedRowBody
            display={{
                ...recentActivityItemDisplay(activity),
                titleAccessory: activity.isImportant ? importantBadge : null,
            }}
            actions={null}
        />
    </li>
);

interface SharedRecentActivitiesProps {
    activities: SharedRecentActivity[];
}

export const SharedRecentActivities: React.FC<SharedRecentActivitiesProps> = ({
    activities,
}) => {
    const [selected, setSelected] = React.useState<RecentActivityFilterKey[]>([]);
    const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE_COUNT);

    const toggle = React.useCallback((key: RecentActivityFilterKey) => {
        setSelected(current =>
            current.includes(key)
                ? current.filter(existing => existing !== key)
                : [...current, key],
        );
        setVisibleCount(INITIAL_VISIBLE_COUNT);
    }, []);

    const clear = React.useCallback(() => {
        setSelected([]);
        setVisibleCount(INITIAL_VISIBLE_COUNT);
    }, []);

    const showMore = React.useCallback(
        () => setVisibleCount(count => count + SHOW_MORE_INCREMENT),
        [],
    );

    const sorted = React.useMemo(() => sortRecentActivityItems(activities), [activities]);
    const filtered = filterRecentActivityItems(sorted, selected);
    const hasMore = filtered.length > visibleCount;
    const reachedEnd = !hasMore && visibleCount > INITIAL_VISIBLE_COUNT;

    return (
        <InputFormBlock>
            <Header>Recent Activities</Header>
            <ActivityFilterChips
                categories={nonVisitActivityCategories}
                selected={selected}
                onToggle={toggle}
                onClear={clear}
            />
            {filtered.length === 0 ? (
                <div className="mt-6">
                    <BlankSlate>
                        {sorted.length === 0
                            ? "No activity yet."
                            : "No activity matches the selected types."}
                    </BlankSlate>
                </div>
            ) : (
                <>
                    <ActivityRowsList className="mt-6" testId="shared-recent-activities">
                        {filtered.slice(0, visibleCount).map(activity => (
                            <SharedRecentActivityRow
                                key={activity.id}
                                activity={activity}
                            />
                        ))}
                    </ActivityRowsList>
                    {hasMore ? (
                        <InteractiveTextLink
                            className="mt-3 inline-flex items-center gap-1 text-sm font-medium"
                            onClick={showMore}>
                            <ChevronDownIcon aria-hidden="true" className="h-4 w-4" />
                            Show more
                        </InteractiveTextLink>
                    ) : null}
                    {reachedEnd ? (
                        <p className="mt-3 text-sm text-gray-500">
                            No more activities to show.
                        </p>
                    ) : null}
                </>
            )}
        </InputFormBlock>
    );
};
